import * as m from '@staiter/i18n';
import { Plus } from 'lucide-react';
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Combobox.module.css';

/**
 * Подписи вариантов, однажды пришедших из `loadOptions`, по значению.
 *
 * Найдено на пакете «Склад» пака `tails-closeout` (22.09): мастер складского
 * документа после «Назад» монтирует пикеры заново, асинхронный список пуст, и
 * выбранное значение печаталось сырым UUID — «0300da9f-9636-…» в «Позиции»,
 * «00000000-…-911» в «Поставщике». Кэш живёт на время вкладки; значения — id
 * сущностей, поэтому между пикерами не пересекаются.
 */
const asyncLabelCache = new Map<string, ComboboxOption>();
/** Запросы подписи по id в полёте — один на id, общий для всех полей с этим значением. */
const resolvePending = new Map<string, Promise<ComboboxOption | null>>();

/** Только для тестов: изоляция кэша между сценариями. */
export function resetComboboxLabelCache(): void {
  asyncLabelCache.clear();
  resolvePending.clear();
}

/** Отступ панели от поля, запас до края окна, потолок и пол высоты (F110 С-I). */
// Круг 5 (UI): 4 px равнялись выносу кольца фокуса (2 + 2) — панель прилипала к кольцу.
const PANEL_GAP = 8;
const PANEL_EDGE = 8;
const PANEL_MAX_H = 480;
const PANEL_MIN_H = 120;

export type ComboboxOption = {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  /**
   * D4.8 — значение варианта (например, «станет 12,3%»), выровненное вправо и
   * набранное моно. Отдельно от `hint`: в хвосте переносящегося описания числа
   * не сопоставляются, а сравнение вариантов и есть смысл такого списка.
   */
  trailing?: string;
  /**
   * F110 пака `tails-closeout` (С-I, гейт UI круга 4): приглушённый хвост подписи в той же строке
   * («(вы)» у своей строки) — отдельным тоном, чтобы не сливаться с подписью.
   */
  suffix?: string;
};

export type ComboboxProps = {
  label: string;
  value?: string | undefined;
  onChange: (value: string) => void;
  onQueryChange?: (q: string) => void;
  /**
   * D8.3 (DR-F15) — раскрытие панели наружу. Поверхность обязана знать о нём,
   * когда под полем стоит подпись, которую панель перекрывает: обрывок текста
   * из-под панели читается как дефект, даже если смысл продублирован внутри
   * списка. Внутреннее состояние остаётся у примитива — наружу идёт только факт.
   */
  onOpenChange?: (open: boolean) => void;
  options?: ComboboxOption[];
  loadOptions?: (q: string) => Promise<ComboboxOption[]>;
  /**
   * Подпись значения, которого нет ни в списке, ни в кэше, — по id с сервера.
   * Нужна, когда поле открывается уже заполненным (восстановленный черновик
   * мастера, форма правки): без неё пользователь видит UUID.
   */
  resolveOption?: (value: string) => Promise<ComboboxOption | null>;
  /**
   * Гейт F59 (C-UX-1): показывать под полем код выбранного варианта (`hint`
   * опции) — когда названия длинные и обрезаются, две строки с одинаковым началом
   * имени иначе не различить. Своя подсказка поля и ошибка — важнее.
   */
  showSelectedHint?: boolean;
  debounceMs?: number;
  placeholder?: string;
  hint?: string;
  /**
   * D4.9, доработка по вердикту дизайн-гейта D4 (BLOCKER от обоих ревьюеров).
   * Пояснение ВНУТРИ раскрытого списка. Хинт под полем не годится: поповер
   * якорится к полю и ложится поверх него — объяснение, ради которого пометки
   * «Недоступен» отсылают к тексту, физически недоступно ровно в тот момент,
   * когда пользователь выбирает.
   */
  listNotice?: ReactNode;
  error?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
  loading?: boolean;
  renderOption?: (opt: ComboboxOption, state: { selected: boolean; active: boolean }) => ReactNode;
  noResultsText?: string;
  clearable?: boolean;
  emptyLabel?: string;
  /** Inline контексты (таблицы) — визуально скрыть label, оставить как aria-labelledby. */
  hideLabel?: boolean;
  /**
   * Inline «создать» из пикера, когда нужного варианта нет в списке. При наличии
   * рисуется action-строка внизу панели «{createLabel}», по клику / Enter (когда
   * ни один вариант не выбран) вызывает `onCreate(текущий запрос)`. Панель закрывается.
   */
  onCreate?: (query: string) => void;
  /** Подпись action-строки создания. Default — `Создать «{q}»`. */
  createLabel?: (query: string) => string;
};

type ComboboxHandle = { focus: () => void };

export const Combobox = forwardRef<ComboboxHandle, ComboboxProps>(function Combobox(
  {
    label,
    value,
    onChange,
    onQueryChange,
    onOpenChange,
    options,
    loadOptions,
    resolveOption,
    showSelectedHint = false,
    debounceMs = 300,
    placeholder = 'Поиск…',
    hint,
    listNotice,
    error,
    disabled,
    required,
    id,
    name,
    className,
    loading: externalLoading,
    renderOption,
    noResultsText = 'Ничего не найдено',
    clearable = true,
    emptyLabel,
    hideLabel = false,
    onCreate,
    createLabel,
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const listboxId = `${inputId}-listbox`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [query, setQuery] = useState('');
  const [openState, setOpenState] = useState(false);
  const open = openState;
  const setOpen = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setOpenState((prev) => {
        const value = typeof next === 'function' ? next(prev) : next;
        if (value !== prev) onOpenChange?.(value);
        return value;
      });
    },
    [onOpenChange],
  );
  const [activeIndex, setActiveIndex] = useState(-1);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState<ComboboxOption[]>([]);
  const [panelPos, setPanelPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    placement: 'bottom' | 'top';
  } | null>(null);
  // S-FIX-5-FE.1.3: portal-to-dialog detection (см. Select). Native `<dialog>` живёт
  // в Top Layer — portal в `document.body` окажется ПОД ним. Чтобы panel был поверх
  // dialog-backdrop, рендерим его как child того же dialog.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }), []);

  const filtered = useMemo(() => {
    if (loadOptions) return asyncOptions;
    const q = query.trim().toLowerCase();
    const src = options ?? [];
    if (!q) return src;
    return src.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint ? o.hint.toLowerCase().includes(q) : false) ||
        o.value.toLowerCase().includes(q),
    );
  }, [options, asyncOptions, query, loadOptions]);

  const [resolved, setResolved] = useState<ComboboxOption | null>(null);
  const selectedOption = useMemo((): ComboboxOption | undefined => {
    if (!value) return undefined;
    const pool = (options ?? []).concat(asyncOptions);
    return (
      pool.find((o) => o.value === value) ??
      (resolved && resolved.value === value ? resolved : undefined) ??
      asyncLabelCache.get(value)
    );
  }, [options, asyncOptions, value, resolved]);
  const selectedLabel = selectedOption?.label ?? (value ? value : '');

  // Флаг «смонтирован», а не отмена в очистке эффекта: эффект перезапускается
  // на любом рендере родителя, и отменённый ответ второй раз не запрашивается.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!value || !resolveOption) return;
    const cached = asyncLabelCache.get(value);
    if (cached) {
      setResolved((prev) => (prev?.value === value ? prev : cached));
      return;
    }
    if ((options ?? []).some((o) => o.value === value)) return;
    if (asyncOptions.some((o) => o.value === value)) return;
    let pending = resolvePending.get(value);
    if (!pending) {
      pending = resolveOption(value).then((opt) => {
        if (opt) asyncLabelCache.set(value, opt);
        return opt;
      });
      resolvePending.set(value, pending);
    }
    pending
      .then((opt) => {
        if (opt && mountedRef.current) setResolved(opt);
      })
      .catch(() => {
        /* подпись — удобство: при отказе сервера поле показывает значение как есть */
      });
  }, [value, resolveOption, options, asyncOptions]);

  const runLoadOptions = useCallback(
    (q: string) => {
      if (!loadOptions) return;
      setAsyncLoading(true);
      loadOptions(q)
        .then((res) => {
          for (const o of res) asyncLabelCache.set(o.value, o);
          setAsyncOptions(res);
        })
        .finally(() => setAsyncLoading(false));
    },
    [loadOptions],
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onQueryChange?.(query);
      if (loadOptions) runLoadOptions(query);
    }, debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, loadOptions, runLoadOptions, onQueryChange, debounceMs]);

  useEffect(() => {
    if (open && loadOptions && asyncOptions.length === 0) {
      runLoadOptions('');
    }
  }, [open, loadOptions, asyncOptions.length, runLoadOptions]);

  // S-FIX-2-FE.2.3: smart positioning (mirror Select.tsx).
  const positionPanel = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    /*
     * F110 пака `tails-closeout` (С-I, гейт UI круга 4, MAJOR): высоту замеряли один раз при
     * открытии, когда `loadOptions` ещё ничего не вернул; после загрузки панель вырастала до
     * `min(60vh, 480px)` и уходила за нижний край окна. Теперь высота — по содержимому
     * (`scrollHeight`), потолок — место до края окна, сторона — где места больше, а пересчёт идёт
     * при каждом изменении размера панели (ResizeObserver ниже).
     */
    const contentH = panelRef.current?.scrollHeight ?? 280;
    const cap = Math.min(PANEL_MAX_H, Math.round(viewportH * 0.6));
    const want = Math.min(contentH, cap);
    const spaceBelow = viewportH - rect.bottom - PANEL_GAP - PANEL_EDGE;
    const spaceAbove = rect.top - PANEL_GAP - PANEL_EDGE;

    let top: number;
    let maxHeight: number;
    let placement: 'bottom' | 'top';
    if (want <= spaceBelow || spaceBelow >= spaceAbove) {
      maxHeight = Math.max(PANEL_MIN_H, Math.min(cap, spaceBelow));
      top = rect.bottom + PANEL_GAP;
      placement = 'bottom';
    } else {
      maxHeight = Math.max(PANEL_MIN_H, Math.min(cap, spaceAbove));
      top = rect.top - PANEL_GAP - Math.min(want, maxHeight);
      placement = 'top';
    }

    let left = rect.left;
    if (left + rect.width > viewportW - 8) {
      left = Math.max(8, viewportW - rect.width - 8);
    }

    setPanelPos((prev) =>
      prev &&
      prev.top === top &&
      prev.left === left &&
      prev.width === rect.width &&
      prev.maxHeight === maxHeight &&
      prev.placement === placement
        ? prev
        : { top, left, width: rect.width, maxHeight, placement },
    );
  }, []);

  // F110 С-I: панель растёт после загрузки вариантов — пересчитать сторону и потолок.
  const panelMounted = open && panelPos !== null;
  useEffect(() => {
    const panel = panelRef.current;
    if (!panelMounted || !panel || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => positionPanel());
    ro.observe(panel);
    for (const child of Array.from(panel.children)) ro.observe(child);
    return () => ro.disconnect();
  }, [panelMounted, positionPanel]);

  useLayoutEffect(() => {
    if (!open) return;
    positionPanel();
    // S-FIX-5-FE.1.3: portal-to-dialog detection.
    const closestDialog = wrapRef.current?.closest('dialog') ?? null;
    setPortalTarget(
      closestDialog instanceof HTMLElement
        ? closestDialog
        : typeof document !== 'undefined'
          ? document.body
          : null,
    );
    const onScroll = () => positionPanel();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (wrapRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) return;
    const activeEl = optionRefs.current[activeIndex];
    if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(filtered.findIndex((o) => o.value === value));
  };

  const pick = (opt: ComboboxOption) => {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const trimmedQuery = query.trim();
  const canCreate = !!onCreate && trimmedQuery.length > 0;
  const triggerCreate = () => {
    if (!canCreate) return;
    onCreate?.(trimmedQuery);
    setOpen(false);
    setQuery('');
  };

  const clear = () => {
    onChange('');
    setQuery('');
    inputRef.current?.focus();
  };

  const handleInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        handleOpen();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => {
        const next = i + 1;
        return next >= filtered.length ? 0 : next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => {
        const next = i - 1;
        return next < 0 ? filtered.length - 1 : next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) pick(opt);
      // Ни один вариант не активен, но введён запрос → inline-создание.
      else if (canCreate) triggerCreate();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const loading = externalLoading || asyncLoading;
  const hasValue = !!value;

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <label
        htmlFor={inputId}
        className={styles.label}
        style={
          hideLabel
            ? {
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clip: 'rect(0 0 0 0)',
              }
            : undefined
        }
      >
        {label}
        {/* FE-107 Sprint Q+: visible required marker для Combobox-based pickers.
            Post-design-review: skip if label already ends with `*` (legacy i18n). */}
        {required && !hideLabel && !(typeof label === 'string' && label.trim().endsWith('*')) && (
          <span className={styles.requiredMark} aria-hidden="true">
            *
          </span>
        )}
      </label>
      <div
        ref={wrapRef}
        className={[
          styles.wrap,
          error ? styles.wrapError : '',
          disabled ? styles.wrapDisabled : '',
          open ? styles.wrapOpen : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <input
          ref={inputRef}
          id={inputId}
          className={styles.input}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
          aria-describedby={describedBy}
          aria-invalid={error ? 'true' : undefined}
          aria-required={required || undefined}
          type="text"
          autoComplete="off"
          placeholder={hasValue ? selectedLabel : placeholder}
          value={open ? query : hasValue ? selectedLabel : ''}
          // Полное имя выбранного — по наведению, когда поле обрезает его многоточием.
          {...(!open && hasValue && selectedLabel ? { title: selectedLabel } : {})}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => {
            if (!open) handleOpen();
          }}
          onKeyDown={handleInputKey}
          disabled={disabled}
          {...(name ? { name } : {})}
        />
        {loading && (
          <span className={styles.spinner} aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <circle
                cx="7"
                cy="7"
                r="5"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="20 12"
              />
            </svg>
          </span>
        )}
        {clearable && hasValue && !loading && !disabled && (
          <button
            type="button"
            className={styles.clear}
            onClick={clear}
            aria-label="Очистить"
            tabIndex={-1}
          >
            ×
          </button>
        )}
        <span className={styles.chevron} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path
              d="M3 4.5l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
      {open && panelPos && portalTarget
        ? createPortal(
            <div
              ref={panelRef}
              data-placement={panelPos.placement}
              className={styles.panel}
              style={{
                top: panelPos.top,
                left: panelPos.left,
                width: panelPos.width,
                maxHeight: panelPos.maxHeight,
              }}
            >
              {loading ? (
                <div className={styles.empty}>
                  <span className={styles.spinner} aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                      <circle
                        cx="7"
                        cy="7"
                        r="5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray="20 12"
                      />
                    </svg>
                  </span>
                  <span>{m.common_loader_loading()}</span>
                </div>
              ) : (
                <>
                  {filtered.length === 0 ? (
                    <div className={styles.empty}>
                      {query ? noResultsText : (emptyLabel ?? 'Введите запрос…')}
                    </div>
                  ) : (
                    <div
                      id={listboxId}
                      role="listbox"
                      aria-labelledby={inputId}
                      className={styles.list}
                      tabIndex={-1}
                    >
                      {listNotice && <div className={styles.listNotice}>{listNotice}</div>}
                      {filtered.map((opt, idx) => {
                        const selected = opt.value === value;
                        const active = idx === activeIndex;
                        return (
                          <div
                            key={opt.value || `__${idx}`}
                            id={`${listboxId}-opt-${idx}`}
                            ref={(el) => {
                              optionRefs.current[idx] = el;
                            }}
                            role="option"
                            tabIndex={-1}
                            aria-selected={selected}
                            aria-disabled={opt.disabled || undefined}
                            className={[
                              styles.option,
                              // Опция с пояснением — вертикальный стек: в строку
                              // длинный hint отжимал НАЗВАНИЕ до нуля, и в списке
                              // оставались одни пояснения (выбор вслепую).
                              opt.hint ? styles.optionStacked : '',
                              selected ? styles.optionSelected : '',
                              active ? styles.optionActive : '',
                              opt.disabled ? styles.optionDisabled : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onMouseEnter={() => !opt.disabled && setActiveIndex(idx)}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              pick(opt);
                            }}
                          >
                            {renderOption ? (
                              renderOption(opt, { selected, active })
                            ) : (
                              <>
                                <span className={styles.optionLabel}>
                                  {opt.label}
                                  {/* Пробел — текстом, не отступом: иначе имя опции читается «Админ(вы)». */}
                                  {opt.suffix && (
                                    <>
                                      {' '}
                                      <span className={styles.optionSuffix}>{opt.suffix}</span>
                                    </>
                                  )}
                                </span>
                                {opt.trailing && (
                                  <span className={styles.optionValue}>{opt.trailing}</span>
                                )}
                                {opt.hint && <span className={styles.optionHint}>{opt.hint}</span>}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {canCreate && (
                    <button
                      type="button"
                      className={styles.createRow}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        triggerCreate();
                      }}
                    >
                      <span className={styles.createPlus} aria-hidden="true">
                        <Plus size={12} strokeWidth={2.5} />
                      </span>
                      {createLabel ? createLabel(trimmedQuery) : `Создать «${trimmedQuery}»`}
                    </button>
                  )}
                </>
              )}
            </div>,
            portalTarget,
          )
        : null}
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {!hint && !error && showSelectedHint && selectedOption?.hint && (
        <p className={[styles.hint, styles.selectedHint].join(' ')}>{selectedOption.hint}</p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});
